import { dateLib, moneyLib } from "@agentcall/core/lib"
import { formatPhone, getContractTypeLabel } from "@agentcall/core/lib/tenant_lib"
import { fileType } from "@agentcall/core/type"
import { useState } from "react"
import { Camera, File, Home, User } from "react-feather"
import { type TenantApplicationModels, tenantApplicationApi } from "@/api/client/tenant-application"
import { EmptyCard } from "@/components/ui/card"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"

// 添付ファイルの型定義
interface AttachedFile {
  id: number
  url: string
  title: string | null
  fileName: string | null
  fileType?: string | null
  tenantFileType: string | null
  createdAt?: string
}

// 承認状況の定義
const APPROVAL_STATUS_LABELS: Record<
  TenantApplicationModels.TenantApplicationGet200TenantApplicationApprovalStatus,
  string
> = {
  pending: "未承認",
  approved: "承認",
  rejected: "棄却",
}

const APPROVAL_STATUS_VARIANTS: Record<
  TenantApplicationModels.TenantApplicationGet200TenantApplicationApprovalStatus,
  "unapproved" | "approved" | "rejected"
> = {
  pending: "unapproved",
  approved: "approved",
  rejected: "rejected",
}

// 用途の定義
const PURPOSE_LABELS: Record<
  TenantApplicationModels.TenantApplicationGet200TenantApplicationPurpose,
  string
> = {
  "1": "居住用",
  "2": "事業用",
}

// 建物構造の定義
const STRUCTURE_LABELS: Record<
  TenantApplicationModels.TenantApplicationGet200TenantApplicationRoomPropertyStructure,
  string
> = {
  "1": "木造",
  "2": "鉄骨造",
  "3": "鉄筋コンクリート造",
  "4": "鉄骨鉄筋コンクリート造",
}

// ファイルタイプのラベルを取得するヘルパー関数
function getFileTypeLabel(tenantFileType: string | null): string | undefined {
  if (!tenantFileType) return undefined
  if (tenantFileType in fileType.TENANT_FILE_TYPE_TITLES) {
    return fileType.TENANT_FILE_TYPE_TITLES[
      tenantFileType as keyof typeof fileType.TENANT_FILE_TYPE_TITLES
    ]
  }
  return undefined
}

// 型ガード関数
function isApprovalStatus(
  value: string
): value is TenantApplicationModels.TenantApplicationGet200TenantApplicationApprovalStatus {
  return value in APPROVAL_STATUS_LABELS
}

function getApprovalStatusLabel(status: string): string {
  if (isApprovalStatus(status)) {
    return APPROVAL_STATUS_LABELS[status]
  }
  return status
}

function getApprovalStatusVariant(status: string): "unapproved" | "approved" | "rejected" {
  if (isApprovalStatus(status)) {
    return APPROVAL_STATUS_VARIANTS[status]
  }
  return "unapproved"
}

const IMAGE_FILE_EXTENSION_REGEX = /\.(jpe?g|png|gif|webp|bmp|svg)$/i
const isImageFile = (fileType: string | null | undefined, fileName?: string | null) =>
  fileType?.startsWith("image/") || (!!fileName && IMAGE_FILE_EXTENSION_REGEX.test(fileName))

const formatOptionalPhone = (
  phone1: string | null | undefined,
  phone2: string | null | undefined,
  phone3: string | null | undefined
) => {
  const phoneParts = [phone1, phone2, phone3].map((phonePart) => phonePart?.trim() ?? "")
  if (phoneParts.every((phonePart) => phonePart.length === 0)) return "-"
  return formatPhone(phoneParts[0], phoneParts[1], phoneParts[2])
}

const formatOccupant = (
  occupant: NonNullable<
    TenantApplicationModels.TenantApplicationGet200TenantApplication["occupants"]
  >[number],
  index: number
) => {
  const occupantWithContact = occupant as typeof occupant & {
    phone?: string | null
    note?: string | null
  }
  return [
    `${index + 1}. ${occupant.name || "-"}`,
    occupant.nameKana ? `(${occupant.nameKana})` : null,
    occupantWithContact.phone ? `電話番号: ${occupantWithContact.phone}` : null,
    occupant.birthday ? `生年月日: ${occupant.birthday}` : null,
    occupantWithContact.note ? `備考: ${occupantWithContact.note}` : null,
  ]
    .filter(Boolean)
    .join(" ")
}

interface TenantApplicantDetailContentProps {
  applicantId: string
}

export function TenantApplicantDetailContent({ applicantId }: TenantApplicantDetailContentProps) {
  const [selectedImage, setSelectedImage] = useState<AttachedFile | null>(null)

  const params = {
    withRoom: "true",
    withRoomProperty: "true",
    withFiles: "true",
  } as const
  const { data } = tenantApplicationApi.useTenantApplicationGetSuspense(applicantId, params)

  const applicant = data.tenantApplication
  const room = applicant.room
  const property = room?.property

  // 料金の計算
  const isBusiness = applicant.purpose === "2"

  const rentTax = isBusiness ? Math.floor(applicant.rent * moneyLib.TAX_RATE) : 0
  const commonAreaFeeTax = isBusiness ? Math.floor(applicant.commonAreaFee * moneyLib.TAX_RATE) : 0

  const fees = [
    {
      label: "家賃",
      amount: applicant.rent + rentTax,
      baseAmount: applicant.rent,
      tax: rentTax,
    },
    {
      label: "共益費・管理費",
      amount: applicant.commonAreaFee + commonAreaFeeTax,
      baseAmount: applicant.commonAreaFee,
      tax: commonAreaFeeTax,
    },
    ...(applicant.isParking
      ? [
          {
            label: "駐車場代",
            amount: applicant.parkingFee + Math.floor(applicant.parkingFee * moneyLib.TAX_RATE),
            baseAmount: applicant.parkingFee,
            tax: Math.floor(applicant.parkingFee * moneyLib.TAX_RATE),
            note: applicant.parkingNumber ? `区画番号：${applicant.parkingNumber}` : undefined,
          },
        ]
      : []),
    ...(applicant.otherFees?.map((fee) => ({
      label: fee.label,
      amount: fee.price + fee.tax,
      baseAmount: fee.price,
      tax: fee.tax,
    })) ?? []),
  ]

  const totalAmount = fees.reduce((sum, fee) => sum + fee.amount, 0)

  // 住所の組み立て
  const buildAddress = () => {
    if (!property) return "-"
    const parts = [property.prefecture, property.city, property.town, property.district].filter(
      Boolean
    )
    return parts.join("") + (room?.name ? ` ${room.name}` : "")
  }

  // 郵便番号の組み立て
  const buildPostalCode = () => {
    if (!property?.upperZip || !property.lowerZip) return "-"
    return `〒${property.upperZip}-${property.lowerZip}`
  }

  return (
    <div className="space-y-3.5">
      {/* Header Block - 申請フォームID & 承認ステータス */}
      <div className="bg-white rounded-lg shadow-sm p-5">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <p className="text-sm text-slate-600">申請フォームID：{applicant.id}</p>
            <div className="flex items-center gap-2.5">
              <span className="text-sm text-slate-600">承認ステータス</span>
              <Label variant={getApprovalStatusVariant(applicant.approvalStatus)}>
                {getApprovalStatusLabel(applicant.approvalStatus)}
              </Label>
              {applicant.submittedAt && (
                <span className="text-sm text-slate-600">
                  （送信完了日時 {dateLib.formatDateTime(applicant.submittedAt)}）
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 契約申請者情報 & 物件情報 */}
      <div className="flex gap-3.5">
        {/* 契約申請者情報 Card */}
        <div className="flex-1 bg-white rounded-lg shadow-sm">
          <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
            <div className="flex items-center gap-1.5">
              <User size={14} />
              <span className="text-sm font-semibold text-slate-600">契約申請者情報</span>
            </div>
            <div className="h-9" />
          </div>
          <div className="p-5 space-y-4">
            <div>
              <div className="text-xs font-medium text-slate-400">契約区分</div>
              <div className="text-sm text-slate-600">
                {getContractTypeLabel(applicant.contractType)}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-400">用途</div>
              <div className="text-sm text-slate-600">
                {PURPOSE_LABELS[applicant.purpose] ?? applicant.purpose}
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="text-xs font-medium text-slate-400">氏名</div>
                <div className="text-sm text-slate-600">{applicant.name}</div>
              </div>
              <div className="flex-1">
                <div className="text-xs font-medium text-slate-400">氏名（カナ）</div>
                <div className="text-sm text-slate-600">{applicant.nameKana}</div>
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-400">電話番号</div>
              <div className="text-sm text-slate-600">
                {formatPhone(applicant.phone1, applicant.phone2, applicant.phone3)}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-400">電話番号2</div>
              <div className="text-sm text-slate-600">
                {formatOptionalPhone(applicant.phone4, applicant.phone5, applicant.phone6)}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-400">メールアドレス</div>
              <div className="text-sm text-slate-600">{applicant.email}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-400">初回賃貸借契約開始日</div>
              <div className="text-sm text-slate-600">{applicant.startDate}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-400">入居者情報</div>
              <div className="space-y-1 text-sm text-slate-600">
                {applicant.occupants && applicant.occupants.length > 0 ? (
                  applicant.occupants.map((occupant, index) => (
                    <div key={`${occupant.name}-${occupant.nameKana}-${occupant.birthday}`}>
                      {formatOccupant(occupant, index)}
                    </div>
                  ))
                ) : (
                  <div>-</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 物件情報 Card */}
        <div className="flex-1 bg-white rounded-lg shadow-sm">
          <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
            <div className="flex items-center gap-1.5">
              <Home size={14} />
              <span className="text-sm font-semibold text-slate-600">物件情報</span>
            </div>
            <div className="h-9" />
          </div>
          <div className="p-5 space-y-4">
            <div>
              <div className="text-xs font-medium text-slate-400">物件名</div>
              <div className="text-sm text-slate-600">{property?.name ?? "-"}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-400">物件名（カナ）</div>
              <div className="text-sm text-slate-600">{property?.nameKana ?? "-"}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-400">部屋番号</div>
              <div className="text-sm text-slate-600">{room?.name ?? "-"}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-400">郵便番号</div>
              <div className="text-sm text-slate-600">{buildPostalCode()}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-400">物件住所</div>
              <div className="text-sm text-slate-600">{buildAddress()}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-400">建物構造</div>
              <div className="text-sm text-slate-600">
                {property?.structure
                  ? (STRUCTURE_LABELS[property.structure] ?? property.structure)
                  : "-"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 部屋情報（料金） & 仲介会社情報 */}
      <div className="flex gap-3.5">
        {/* 部屋情報 Card */}
        <div className="flex-1 bg-white rounded-lg shadow-sm">
          <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
            <div className="flex items-center gap-1.5">
              <Home size={14} />
              <span className="text-sm font-semibold text-slate-600">部屋情報</span>
            </div>
            <div className="h-9" />
          </div>
          <div className="p-5">
            <div className="space-y-2.5">
              {fees.map((fee, index) => (
                <div key={fee.label}>
                  <div className="flex justify-between items-start">
                    <div className="text-sm font-medium text-slate-400">{fee.label}</div>
                    <div className="flex flex-col items-end">
                      {"note" in fee && fee.note && (
                        <div className="text-sm text-slate-600">{fee.note}</div>
                      )}
                      <div className="text-base font-medium text-slate-600">
                        ¥{fee.amount.toLocaleString("ja-JP")}
                      </div>
                      {"tax" in fee && fee.tax > 0 && (
                        <div className="text-sm text-slate-400">
                          ¥{("baseAmount" in fee ? fee.baseAmount : 0).toLocaleString("ja-JP")} +
                          税¥
                          {fee.tax.toLocaleString("ja-JP")}
                        </div>
                      )}
                    </div>
                  </div>
                  {index < fees.length - 1 && <hr className="my-2.5 border-slate-200" />}
                </div>
              ))}
              <hr className="my-2.5 border-slate-200" />
              <div className="flex justify-between items-start">
                <div className="text-sm font-medium text-slate-400">月額合計</div>
                <div className="flex flex-col items-end">
                  <div className="text-base font-medium text-slate-600">
                    ¥{totalAmount.toLocaleString("ja-JP")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 仲介会社情報 Card */}
        <div className="flex-1 bg-white rounded-lg shadow-sm">
          <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
            <div className="flex items-center gap-1.5">
              <File size={14} />
              <span className="text-sm font-semibold text-slate-600">仲介会社情報</span>
            </div>
            <div className="h-9" />
          </div>
          <div className="p-5 space-y-4">
            <div>
              <div className="text-xs font-medium text-slate-400">仲介会社名</div>
              <div className="text-sm text-slate-600">{applicant.agencyName || "-"}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-400">電話番号</div>
              <div className="text-sm text-slate-600">{applicant.agencyPhone || "-"}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-400">その他連絡先</div>
              <div className="text-sm text-slate-600">{applicant.otherContact || "-"}</div>
            </div>
          </div>
        </div>
      </div>

      {/* 添付ファイル Card */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
          <div className="flex items-center gap-1.5">
            <Camera size={14} />
            <span className="text-sm font-semibold text-slate-600">添付ファイル</span>
          </div>
          <div className="h-9" />
        </div>
        <div className="p-5">
          {applicant.files && applicant.files.length > 0 ? (
            <div className="grid grid-cols-2 gap-4">
              {applicant.files.map((file: AttachedFile) => (
                <button
                  key={file.id}
                  type="button"
                  onClick={() => setSelectedImage(file)}
                  className="flex gap-[15px] items-start p-[15px] bg-white border border-slate-200 rounded-[5px] hover:bg-slate-50 transition-colors text-left cursor-pointer"
                >
                  <div className="flex-1 flex flex-col gap-[7px] min-w-0">
                    <p className="text-xs font-medium text-slate-400 leading-[14px] tracking-[-0.144px]">
                      アップロード日：{file.createdAt || "-"}
                    </p>
                    <p className="text-sm font-medium text-slate-600 leading-5 tracking-[-0.168px] truncate">
                      {file.title ||
                        getFileTypeLabel(file.tenantFileType) ||
                        file.fileName ||
                        "添付ファイル"}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    {isImageFile(file.fileType, file.fileName) ? (
                      <img
                        src={file.url}
                        alt={file.title || file.fileName || "添付ファイル"}
                        className="w-[188px] h-[141px] object-cover rounded-[6px]"
                      />
                    ) : (
                      <div className="flex w-[188px] h-[141px] flex-col items-center justify-center gap-2 rounded-[6px] bg-slate-100 p-3 text-slate-600">
                        <File size={32} className="text-slate-400" />
                        <span className="w-full truncate text-center text-xs font-medium">
                          {file.fileName || file.title || "添付ファイル"}
                        </span>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <EmptyCard text="添付ファイルはありません" />
          )}
        </div>
      </div>

      {/* 添付ファイル表示ダイアログ */}
      <Dialog open={!!selectedImage} onOpenChange={(open) => !open && setSelectedImage(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          <DialogTitle className="sr-only">
            {selectedImage?.title ||
              getFileTypeLabel(selectedImage?.tenantFileType ?? null) ||
              selectedImage?.fileName ||
              "添付ファイル"}
          </DialogTitle>
          {selectedImage && (
            <div className="flex flex-col">
              <div className="p-4 border-b border-slate-200">
                <p className="text-xs font-medium text-slate-400">
                  アップロード日：{selectedImage.createdAt || "-"}
                </p>
                <p className="text-sm font-medium text-slate-600 mt-1">
                  {selectedImage.title ||
                    getFileTypeLabel(selectedImage.tenantFileType) ||
                    selectedImage.fileName ||
                    "添付ファイル"}
                </p>
              </div>
              <div className="flex items-center justify-center bg-slate-100 p-4">
                {isImageFile(selectedImage.fileType, selectedImage.fileName) ? (
                  <img
                    src={selectedImage.url}
                    alt={selectedImage.title || selectedImage.fileName || "添付ファイル"}
                    className="max-w-full max-h-[70vh] object-contain"
                  />
                ) : (
                  <a
                    href={selectedImage.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex min-h-[240px] w-full flex-col items-center justify-center gap-3 rounded-md bg-white p-6 text-slate-600 hover:bg-slate-50"
                  >
                    <File size={48} className="text-slate-400" />
                    <span className="max-w-full break-all text-center text-sm font-medium">
                      {selectedImage.fileName || selectedImage.title || "添付ファイル"}
                    </span>
                  </a>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
